import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { authState, AuthState } from '../store/atoms';
import { cookieAuth } from '../utils/cookieAuth';
import { authAPI, userAPI } from '../services';
import { Teacher, User } from '../types/api';
import { initWebPush, cleanupPushToken } from '../services/pushService';

// 디바운싱을 위한 타이머
let refreshTimer: NodeJS.Timeout | null = null;
let isRefreshing = false;

// 전역 토큰 검증 상태 관리
let globalValidationState = {
  isValidating: false,
  lastValidation: 0,
  validationCache: new Map<string, { isValid: boolean; timestamp: number }>()
};

// 캐시 유효 시간 (10분으로 증가하여 API 호출 빈도 감소)
const VALIDATION_CACHE_DURATION = 10 * 60 * 1000;

export const useAuth = () => {
  const [auth, setAuth] = useAtom(authState);
  const navigate = useNavigate();

  // 페이지 로드 시 토큰 유효성 자동 체크
  useEffect(() => {
    const checkTokenOnLoad = async () => {
      // 이미 인증된 상태이고 사용자 정보가 있는 경우에만 체크
      if (auth.isAuthenticated && auth.userInfo && !isRefreshing) {
        try {
          const isValid = await validateToken();
          if (!isValid) {
            // 즉시 로그아웃 처리
            cookieAuth.clearUserInfo();
            setAuth({
              isAuthenticated: false,
              userType: null,
              userInfo: null,
              loading: false,
            });

            // 쿠키 강제 삭제
            cookieAuth.clearAuthTokens();

            // 보호된 경로(/admin, /user)에서만 로그인 페이지로 리다이렉트
            const pathname = window.location.pathname;
            if (pathname.startsWith('/admin') || pathname.startsWith('/user')) {
              navigate('/login');
            }
          }
        } catch {
          // 에러 발생 시에도 즉시 로그아웃 처리
          cookieAuth.clearUserInfo();
          setAuth({
            isAuthenticated: false,
            userType: null,
            userInfo: null,
            loading: false,
          });

          // 쿠키 강제 삭제
          document.cookie = 'accessToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
          document.cookie = 'refreshToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';

          // 보호된 경로(/admin, /user)에서만 로그인 페이지로 리다이렉트
          const pathname = window.location.pathname;
          if (pathname.startsWith('/admin') || pathname.startsWith('/user')) {
            navigate('/login');
          }
        }
      }
    };

    // 페이지 로드 후 약간의 지연을 두고 토큰 검증 (2초로 증가하여 안정성 향상)
    const timer = setTimeout(checkTokenOnLoad, 2000);
    return () => clearTimeout(timer);
  }, [auth.isAuthenticated, auth.userInfo]);

  // 로그인 처리
  const login = async (credentials: { loginId: string; password: string }) => {
    try {
      setAuth(prev => ({ ...prev, loading: true }));

      const response = await authAPI.login(credentials);

      if (response.success && response.data) {
        const { userType, user: userInfo } = response.data;
        // 로그인 직후 verify API 호출해서 전체 사용자 정보 가져오기
        try {
          const verifyResponse = await authAPI.verifyToken();

          if (verifyResponse.success && verifyResponse.data) {
            const { user: fullUserInfo } = verifyResponse.data;

            // 전체 정보로 쿠키 및 state 업데이트
            cookieAuth.setUserInfo(fullUserInfo, userType);
            setAuth({
              isAuthenticated: true,
              userType,
              userInfo: fullUserInfo,
              loading: false,
            });
          } else {
            // verify 실패 시 로그인 응답의 제한된 정보라도 저장
            cookieAuth.setUserInfo(userInfo, userType);
            setAuth({
              isAuthenticated: true,
              userType,
              userInfo,
              loading: false,
            });
          }
        } catch {
          // verify 실패 시 로그인 응답의 제한된 정보라도 저장
          cookieAuth.setUserInfo(userInfo, userType);
          setAuth({
            isAuthenticated: true,
            userType,
            userInfo,
            loading: false,
          });
        }

        // 로그인 성공 시 검증 캐시 초기화
        globalValidationState.validationCache.clear();
        globalValidationState.lastValidation = Date.now();

        // 푸시 알림 토큰 등록
        try {
          console.log(`[PUSH-DEBUG] useAuth: 로그인 성공 → initWebPush(${userType}) 호출`);
          await initWebPush(userType);
          console.log('[PUSH-DEBUG] useAuth: initWebPush 완료');
        } catch (pushErr) {
          console.error('[PUSH-DEBUG] useAuth: 푸시 토큰 등록 실패:', pushErr);
        }

        return response;
      }

      throw new Error('로그인 응답 데이터가 올바르지 않습니다.');
    } catch (error) {
      setAuth(prev => ({ ...prev, loading: false }));
      throw error;
    }
  };

  // 로그아웃 처리
  const logout = async () => {
    try {
      // 푸시 토큰 비활성화
      if (auth.userType) {
        await cleanupPushToken(auth.userType).catch(() => {});
      }
      await authAPI.logout();
    } catch {
      // logout error ignored
    } finally {
      // 로그아웃 시 상태 및 캐시 정리
      cookieAuth.clearUserInfo();
      setAuth({
        isAuthenticated: false,
        userType: null,
        userInfo: null,
        loading: false,
      });
      globalValidationState.validationCache.clear();
      globalValidationState.lastValidation = 0;
      navigate('/login');
    }
  };

  // 사용자 정보 업데이트
  const updateUserInfo = (userInfo: User | Teacher) => {
    if (auth.userType) {
      cookieAuth.setUserInfo(userInfo, auth.userType);
      setAuth(prev => ({
        ...prev,
        userInfo
      }));

      // 프로필 캐시 무효화
      userAPI.invalidateProfileCache();

      // 검증 캐시도 무효화
      globalValidationState.validationCache.clear();
    }
  };

  // 토큰 검증 및 사용자 정보 새로고침 (디바운싱 및 캐싱 적용)
  const refreshUserInfo = async (): Promise<boolean> => {
    // 이미 새로고침 중이면 기존 결과 대기
    if (isRefreshing) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!isRefreshing) {
            clearInterval(checkInterval);
            resolve(auth.isAuthenticated);
          }
        }, 100);
        
        // 5초 후 타임아웃
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(false);
        }, 5000);
      });
    }

    // 디바운싱: 500ms 내 연속 호출 방지 (1초에서 500ms로 단축)
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      return new Promise((resolve) => {
        refreshTimer = setTimeout(async () => {
          const result = await refreshUserInfo();
          resolve(result);
        }, 500);
      });
    }

    // 캐시된 검증 결과가 유효한지 확인
    const now = Date.now();
    const cacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
    const cachedResult = globalValidationState.validationCache.get(cacheKey);

    if (cachedResult && (now - cachedResult.timestamp) < VALIDATION_CACHE_DURATION) {
      return cachedResult.isValid;
    }

    // 전역 검증 상태 확인 (다른 컴포넌트에서 이미 검증 중인지)
    if (globalValidationState.isValidating) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!globalValidationState.isValidating) {
            clearInterval(checkInterval);
            const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
            const finalCachedResult = globalValidationState.validationCache.get(finalCacheKey);
            resolve(finalCachedResult ? finalCachedResult.isValid : false);
          }
        }, 100);
      });
    }

    try {
      isRefreshing = true;
      globalValidationState.isValidating = true;
      setAuth(prev => ({ ...prev, loading: true }));

      const response = await authAPI.verifyToken();

      if (response.success && response.data) {
        const { userType, user: userInfo } = response.data;

        // 쿠키 업데이트
        cookieAuth.setUserInfo(userInfo, userType);

        // store 업데이트
        const nextAuth: AuthState = {
          isAuthenticated: true,
          userType,
          userInfo,
          loading: false,
        };
        setAuth(nextAuth);

        // 검증 결과 캐시에 저장
        const finalCacheKey = `${userType}-${getUserId(userInfo)}`;
        globalValidationState.validationCache.set(finalCacheKey, {
          isValid: true,
          timestamp: now
        });
        globalValidationState.lastValidation = now;

        return true;
      }

      // 토큰이 유효하지 않으면 즉시 로그아웃 처리
      cookieAuth.clearUserInfo();
      setAuth({
        isAuthenticated: false,
        userType: null,
        userInfo: null,
        loading: false,
      });

      // 쿠키 강제 삭제
      cookieAuth.clearAuthTokens();

      // 보호된 경로(/admin, /user)에서만 로그인 페이지로 리다이렉트
      const pathname = window.location.pathname;
      if (pathname.startsWith('/admin') || pathname.startsWith('/user')) {
        navigate('/login');
      }

      // 검증 실패 결과 캐시에 저장
      const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
      globalValidationState.validationCache.set(finalCacheKey, {
        isValid: false,
        timestamp: now
      });

      return false;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number }; code?: string; message?: string };

      // 401 에러인 경우 즉시 로그아웃 처리
      if (axiosError.response?.status === 401) {
        cookieAuth.clearUserInfo();
        setAuth({
          isAuthenticated: false,
          userType: null,
          userInfo: null,
          loading: false,
        });

        // 쿠키 강제 삭제
        document.cookie = 'accessToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        document.cookie = 'refreshToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';

        // 보호된 경로(/admin, /user)에서만 로그인 페이지로 리다이렉트
        const pathname = window.location.pathname;
        if (pathname.startsWith('/admin') || pathname.startsWith('/user')) {
          navigate('/login');
        }
        return false;
      }

      // 429 에러(Too Many Requests)인 경우 더 긴 시간 동안 캐시 유지
      if (axiosError.response?.status === 429) {
        const cacheKey429 = `${auth.userType}-${getUserId(auth.userInfo)}`;
        globalValidationState.validationCache.set(cacheKey429, {
          isValid: auth.isAuthenticated,
          timestamp: now - (VALIDATION_CACHE_DURATION * 0.8)
        });
        return auth.isAuthenticated;
      }

      // 에러 발생 시에도 검증 실패 결과 캐시에 저장
      const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
      globalValidationState.validationCache.set(finalCacheKey, {
        isValid: false,
        timestamp: now
      });

      return false;
    } finally {
      isRefreshing = false;
      globalValidationState.isValidating = false;
      setAuth(prev => ({ ...prev, loading: false }));
    }
  };

  // 토큰 검증만 수행 (사용자 정보 업데이트 없음)
  const validateToken = async (): Promise<boolean> => {
    // 캐시된 검증 결과가 유효한지 확인
    const now = Date.now();
    const cacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
    const cachedResult = globalValidationState.validationCache.get(cacheKey);

    if (cachedResult && (now - cachedResult.timestamp) < VALIDATION_CACHE_DURATION) {
      return cachedResult.isValid;
    }

    // 전역 검증 상태 확인
    if (globalValidationState.isValidating) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!globalValidationState.isValidating) {
            clearInterval(checkInterval);
            const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
            const finalCachedResult = globalValidationState.validationCache.get(finalCacheKey);
            resolve(finalCachedResult ? finalCachedResult.isValid : false);
          }
        }, 100);
      });
    }

    try {
      globalValidationState.isValidating = true;

      const response = await authAPI.verifyToken();
      const isValid = response.success && response.data?.userType === auth.userType;

      // 검증 결과 캐시에 저장
      const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
      globalValidationState.validationCache.set(finalCacheKey, {
        isValid,
        timestamp: now
      });
      globalValidationState.lastValidation = now;

      return isValid;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number }; code?: string; message?: string };

      // 401 에러인 경우 즉시 로그아웃 처리
      if (axiosError.response?.status === 401) {
        return false;
      }

      // 429 에러(Too Many Requests)인 경우 더 긴 시간 동안 캐시 유지
      if (axiosError.response?.status === 429) {
        const cacheKey429 = `${auth.userType}-${getUserId(auth.userInfo)}`;
        globalValidationState.validationCache.set(cacheKey429, {
          isValid: auth.isAuthenticated,
          timestamp: now - (VALIDATION_CACHE_DURATION * 0.8)
        });
        return auth.isAuthenticated;
      }

      // 네트워크 오류나 기타 오류의 경우 현재 상태 유지
      if (axiosError.code === 'NETWORK_ERROR' || axiosError.message?.includes('Network Error')) {
        const networkCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
        globalValidationState.validationCache.set(networkCacheKey, {
          isValid: auth.isAuthenticated,
          timestamp: now - (VALIDATION_CACHE_DURATION * 0.5)
        });
        return auth.isAuthenticated;
      }

      // 에러 발생 시에도 검증 실패 결과 캐시에 저장
      const finalCacheKey = `${auth.userType}-${getUserId(auth.userInfo)}`;
      globalValidationState.validationCache.set(finalCacheKey, {
        isValid: false,
        timestamp: now
      });

      return false;
    } finally {
      globalValidationState.isValidating = false;
    }
  };

  // 사용자 ID를 안전하게 가져오는 헬퍼 함수
  const getUserId = (userInfo: User | Teacher | null): string | number | undefined => {
    if (!userInfo) return undefined;

    if ('teacherId' in userInfo) {
      return userInfo.teacherId || undefined;
    } else if ('userId' in userInfo) {
      return userInfo.userId;
    }

    return undefined;
  };

  return {
    auth,
    login,
    logout,
    updateUserInfo,
    refreshUserInfo,
    validateToken,
    isAuthenticated: auth.isAuthenticated,
    isUser: auth.userType === 'user',
    isTeacher: auth.userType === 'teacher',
    isLoading: auth.loading,
    userInfo: auth.userInfo
  };
};

export default useAuth;